/**
 * Sentry chargé APRÈS l'affichage, sans perdre les erreurs du démarrage (27/10/2026).
 *
 * Mesure en production : `@sentry/react` pesait ≈ 121 Ko (non compressé) des 733 Ko
 * de `main.js`, téléchargés et exécutés avant le premier affichage de CHAQUE page
 * (1,8 s pour `main.js` en 4G). Aucun module ne l'importe plus statiquement : le
 * bundler le sort dans un fragment séparé, chargé par `chargerSentry` une fois la
 * page affichée (doc Sentry « Lazy Loading »).
 *
 * Tant qu'il n'est pas chargé, chaque appel passe par `avecSentry(fn)` et attend
 * dans une file (bornée) ; il est rejoué dès le chargement, dans l'ordre. Une
 * erreur levée pendant ces premières secondes n'est donc pas perdue.
 */
const FILE_MAX = 50;
let sdk = null;
let chargement = null;
const file = [];

const executer = (fn) => {
  try { fn(sdk); } catch (_) { /* Sentry ne casse jamais l'appelant */ }
};

/** Exécute `fn(Sentry)` maintenant si le SDK est prêt, sinon dès son chargement. */
export function avecSentry(fn) {
  if (sdk) { executer(fn); return; }
  if (file.length < FILE_MAX) file.push(fn);
}

/** Vrai une fois le SDK chargé et initialisé. */
export const sentryPret = () => sdk !== null;

/**
 * Charge le SDK (une seule fois), appelle `initialiser(Sentry)` puis rejoue la
 * file. Si le chargement échoue (bloqueur, réseau), la file est abandonnée :
 * le signalement serveur (`/client-log`) garde la trace des erreurs.
 */
export function chargerSentry(initialiser, importer = () => import("@sentry/react")) {
  if (!chargement) {
    chargement = importer()
      .then((S) => {
        try { initialiser(S); } catch (_) { /* navigateur sans stockage, etc. */ }
        sdk = S;
        file.splice(0).forEach(executer);
        return S;
      })
      .catch(() => { file.length = 0; return null; });
  }
  return chargement;
}

/** Réservé aux tests. */
export function _reinitialiserPourTests() {
  sdk = null; chargement = null; file.length = 0;
}
